/**
 * 소자 단면 그림 ↔ 데이터 층 매핑 (M3D). docs/data-visualization-plan-m3d.md §2-7.
 *
 * 핵심 재료 5종(사용자 지정 2026-10-26): 기판 / gate dielectric / n- 또는 p-type active layer / source-drain electrode / passivation layer.
 * 그림 구조상 게이트 전극(과 M3D 의 buffer·ILD·planarization)도 층으로 그리되 핵심 재료 밖(core=false)으로 둔다.
 *
 * 키: 공정도 id(계열). layers 는 "쌓는 순서"(아래 → 위)로 나열한다. DvDeviceFigure 가 이 순서대로 증착을 흉내 내어 단면을 그린다.
 *   key     : 층 식별자(그림·패널 공유)
 *   label   : 표시 이름
 *   core    : 핵심 재료 여부(사용자 지정 5종만 true)
 *   draw    : 그리는 방식 — "substrate"(바닥 전면) | "gate"(가운데 사다리꼴) | "conformal"(아래 형상을 따라 전면 덮음: 절연막·패시베이션)
 *             | "active"(게이트 위 반도체 섬) | "sd"(좌우 두 전극, 절연막 경사면을 타고 반도체 끝을 덮음) | "band"(전면 평평한 층: buffer·전극·유전층)
 *             | "pad"(가운데 넓은 사다리꼴 전극: MIM 상부 전극)
 *             | "planar"(전면 덮고 평탄화: ILD/planarization — 다음 층은 평평한 면 위에 쌓임)
 *   tier    : (M3D) 단 이름 — 패널·범례에서 묶어 보여 준다
 *   sources : 이 층의 물질명을 담은 속성 [{ node, order?, prop }]
 *             node = 공정도 노드명(앞뒤 공백 무시), order = 같은 이름이 여럿일 때 공정 순서(dictionary_key_links.node_order), prop = 속성명
 *             같은 층을 만드는 대체 공정(ALD/스퍼터/CVD/스핀코팅 …)은 모두 나열 — 어느 공정이든 값이 있으면 그 층의 물질이다
 * 근거: dv-search-tree-m3d.md 공정 흐름표(속성 0 인 MATERIAL 노드 = 증착·형성된 층, 바로 앞 PROCESS 노드가 만든 공정) + dev 실데이터 값 확인(2026-10-26).
 *   n형: Substrate/substrate = Si/SiO2·Si·Glass, Thermal evaporation/materials = Si·ITO(게이트), Deposition (PECVD)/material = SiO2·HfO2·Al2O3(게이트 절연막),
 *        ALD/material = In2O3·IGO, sputter/target = IGZO…, spin coating/material = Cd2SnO4…(활성층), Deposition(18)/material = Ni/Au·ITO·Al(S/D), Deposition(20)/material = Al2O3(패시베이션)
 *   p형: Substrate/substrate type, Thermal evaporation/materials = Si++·Au·Ni(게이트), ALD(4)/material = Al2O3·SiO2(게이트 절연막),
 *        ALD(7)/material = TeSeO·Te·SnO, sputter/target = TeSe, spin coating/material = CuI·Perovskite(활성층), Deposition(18)/material = Ni·Au(S/D), ALD(20)/material = Al2O3(패시베이션)
 *   M3D(8267, 436건): Deposition(2) SiO2(buffer), sputter(5) Al:InZnSnO(n 활성층), Deposition(7) SiO2(NMOS 절연막), Deposition(9) Mo(NMOS 게이트), Deposition(11) ILD(값 없음),
 *        Deposition(13) Mo(NMOS S/D), planarization(16) SiO2, Deposition(17) Mo(PMOS 게이트), Deposition(19) Al2O3(PMOS 절연막), Deposition(21) Mo/ITO(PMOS S/D), ALD(23) SnO(p 활성층)
 *        ※ 데이터의 NMOS 는 활성층→절연막→게이트 순(top-gate)이지만 그림은 사용자 지시대로 참조 TFT 단면을 세로로 반복한다.
 */
const TFT = (type) => [
  { key: "substrate", label: "기판", core: true, draw: "substrate",
    sources: [{ node: "Substrate", prop: "substrate" }, { node: "Substrate", prop: "substrate type" }, { node: "Substrate", prop: "material" }] },
  { key: "gate", label: "게이트", core: false, draw: "gate",
    sources: [{ node: "Thermal evaporation", prop: "materials" }] },
  { key: "gate_dielectric", label: "게이트 절연막", core: true, draw: "conformal",
    sources: type === "n"
      ? [{ node: "Deposition (PECVD)", prop: "material" }]
      : [{ node: "ALD", order: 4, prop: "material" }] },
  { key: "active", label: type === "n" ? "n형 활성층 (반도체)" : "p형 활성층 (반도체)", core: true, draw: "active",
    sources: [
      { node: "ALD", order: 7, prop: "material" },
      { node: "sputter deposition", prop: "target" },
      { node: "CVD", prop: "precursor name" },
      { node: "Evaporation", prop: "Source1 material" },
      { node: "spin coating", prop: "material" },
    ] },
  { key: "sd", label: "소스·드레인 전극", core: true, draw: "sd",
    sources: [{ node: "Deposition", order: 18, prop: "material" }] },
  { key: "passivation", label: "패시베이션", core: true, draw: "conformal",
    sources: type === "n"
      ? [{ node: "Deposition", order: 20, prop: "material" }]
      : [{ node: "ALD", order: 20, prop: "material" }] },
];

// M3D 소자: n-TFT 위에 ILD·평탄화를 두고 p-TFT 를 같은 모양으로 반복
const M3D = [
  { key: "substrate", label: "기판", core: true, draw: "substrate", tier: "n-TFT (하부)",
    // M3D 공정도의 Substrate 노드는 속성이 "레시피 출처"(값 Si) 하나뿐 — 이름은 잘못됐지만 기판 물질이 들어 있어 함께 본다(2026-10-27 실데이터)
    sources: [{ node: "Substrate", prop: "substrate" }, { node: "Substrate", prop: "material" }, { node: "Substrate", prop: "substrate type" }, { node: "Substrate", prop: "레시피 출처" }] },
  { key: "n_buffer", label: "buffer", core: false, draw: "band", tier: "n-TFT (하부)",
    sources: [{ node: "Deposition", order: 2, prop: "material" }] },
  { key: "n_gate", label: "NMOS 게이트", core: false, draw: "gate", tier: "n-TFT (하부)",
    sources: [{ node: "Deposition", order: 9, prop: "material" }] },
  { key: "n_gate_dielectric", label: "NMOS 게이트 절연막", core: true, draw: "conformal", tier: "n-TFT (하부)",
    sources: [{ node: "Deposition", order: 7, prop: "material" }] },
  { key: "n_active", label: "n형 활성층 (반도체)", core: true, draw: "active", tier: "n-TFT (하부)",
    sources: [{ node: "ALD", order: 4, prop: "material" }, { node: "sputter deposition", order: 5, prop: "target" }] },
  { key: "n_sd", label: "NMOS 소스·드레인", core: true, draw: "sd", tier: "n-TFT (하부)",
    sources: [{ node: "Deposition", order: 13, prop: "material" }] },
  { key: "ild", label: "ILD · 평탄화", core: false, draw: "planar", tier: "층간",
    sources: [{ node: "Deposition", order: 11, prop: "material" }, { node: "planarization", order: 16, prop: "Materials" }] },
  { key: "p_gate", label: "PMOS 게이트", core: false, draw: "gate", tier: "p-TFT (상부)",
    sources: [{ node: "Deposition", order: 17, prop: "material" }] },
  { key: "p_gate_dielectric", label: "PMOS 게이트 절연막", core: true, draw: "conformal", tier: "p-TFT (상부)",
    sources: [{ node: "Deposition", order: 19, prop: "material" }] },
  { key: "p_active", label: "p형 활성층 (반도체)", core: true, draw: "active", tier: "p-TFT (상부)",
    sources: [{ node: "ALD", order: 23, prop: "material" }, { node: "sputter deposition", order: 24, prop: "target" }] },
  { key: "p_sd", label: "PMOS 소스·드레인", core: true, draw: "sd", tier: "p-TFT (상부)",
    sources: [{ node: "Deposition", order: 21, prop: "material" }] },
];

// 적층소재(8264, 272건): MIM(금속-유전층-금속) 커패시터 — 기판 Si++ / 하부 전극(Device analysis: Si·TiN) / 유전층(ALD hfo2·HZO·ZrO2, 스핀코팅 전구체 …) / 상부 전극(Au·TiN·Al)
//   전극 물질은 공정 노드가 아니라 Device analysis(분석·성능 노드)의 속성에만 있다 → 그 속성을 층에 연결
const MIM = [
  { key: "substrate", label: "기판", core: true, draw: "substrate",
    sources: [{ node: "Substrate", prop: "material" }, { node: "Substrate", prop: "substrate" }, { node: "Device analysis", prop: "substrate material" }] },
  { key: "bottom_electrode", label: "하부 전극", core: true, draw: "band",
    sources: [{ node: "Device analysis", prop: "Bottom electrode material" }] },
  { key: "dielectric", label: "유전층", core: true, draw: "band",
    sources: [
      { node: "ALD", prop: "material" },
      { node: "spin coating", prop: "material" },
      { node: "spin coating", prop: "precursor name" },
      { node: "sputter deposition", prop: "target" },
      { node: "CVD", prop: "precursor name" },
      { node: "Evaporation", prop: "Source1 material" },
    ] },
  { key: "top_electrode", label: "상부 전극", core: true, draw: "pad",
    sources: [{ node: "Device analysis", prop: "Top electrode material" }] },
];

export default {
  // n형 반도체
  8132: { name: "n형 TFT (bottom gate)", layers: TFT("n") },
  // p형 반도체
  8262: { name: "p형 TFT (bottom gate)", layers: TFT("p") },
  // M3D 소자: n-TFT / p-TFT 수직 적층
  8267: { name: "M3D — n-TFT / p-TFT 수직 적층", layers: M3D },
  // 적층소재: MIM 커패시터
  8264: { name: "적층소재 — MIM 커패시터", layers: MIM },
  // 계산 데이터: 소자 구조가 아님 — 그림 없음
};
